import { create } from 'zustand';

export type ThemeMode = 'dark' | 'light';

export interface ThemeConfig {
  mode: ThemeMode;
  colors: {
    bg: {
      primary: string;
      secondary: string;
      tertiary: string;
    };
    text: {
      primary: string;
      secondary: string;
      tertiary: string;
    };
    accent: string;
    accentHover: string;
    border: string;
  };
}

export const DARK_THEME: ThemeConfig = {
  mode: 'dark',
  colors: {
    bg: {
      primary: '#0A0A0F',
      secondary: 'rgba(255, 255, 255, 0.05)',
      tertiary: 'rgba(255, 255, 255, 0.02)',
    },
    text: {
      primary: '#FFFFFF',
      secondary: 'rgba(255, 255, 255, 0.7)',
      tertiary: 'rgba(255, 255, 255, 0.3)',
    },
    accent: '#C8A96E',
    accentHover: '#D4B87A',
    border: 'rgba(255, 255, 255, 0.1)',
  },
};

export const LIGHT_THEME: ThemeConfig = {
  mode: 'light',
  colors: {
    bg: {
      primary: '#FFFFFF',
      secondary: 'rgba(10, 10, 15, 0.05)',
      tertiary: 'rgba(10, 10, 15, 0.02)',
    },
    text: {
      primary: '#0A0A0F',
      secondary: 'rgba(10, 10, 15, 0.7)',
      tertiary: 'rgba(10, 10, 15, 0.3)',
    },
    accent: '#C8A96E',
    accentHover: '#D4B87A',
    border: 'rgba(10, 10, 15, 0.1)',
  },
};

interface ThemeState {
  mode: ThemeMode;
  theme: ThemeConfig;
  setTheme: (mode: ThemeMode) => void;
  toggleTheme: () => void;
}

export const useThemeStore = create<ThemeState>((set) => {
  const storedMode = (window.localStorage.getItem('theme_mode') || 'dark') as ThemeMode;
  const initialTheme = storedMode === 'light' ? LIGHT_THEME : DARK_THEME;

  return {
    mode: storedMode,
    theme: initialTheme,

    setTheme: (mode: ThemeMode) => {
      const theme = mode === 'light' ? LIGHT_THEME : DARK_THEME;
      window.localStorage.setItem('theme_mode', mode);
      set({ mode, theme });
    },

    toggleTheme: () => {
      set((state) => {
        const newMode = state.mode === 'dark' ? 'light' : 'dark';
        const newTheme = newMode === 'light' ? LIGHT_THEME : DARK_THEME;
        window.localStorage.setItem('theme_mode', newMode);
        return { mode: newMode, theme: newTheme };
      });
    },
  };
});
